import { mapMutations, mapActions, mapState } from 'vuex';

import { widgetViews, setCampaignInUrl, unparam, MAX_AMOUNT } from '@/utils';

export default {
  computed: {
    ...mapState(['isPages']),
    ...mapState('publicOrgSettings', {
      globalSettings: (state) => state.settings
    }),
    ...mapState('donation', {
      donation: (state) => state
    }),
    ...mapState('campaign', {
      campaign: (state) => state
    })
  },

  methods: {
    ...mapActions(['setWidgetOpen']),
    ...mapActions('fundraiser', ['fetchFundraiser']),
    ...mapMutations(['SET_WIDGET_VIEW', 'SET_SHOW_PAYMENT_BUTTONS', 'SET_CUSTOM_AMOUNT_OPEN']),
    ...mapMutations('donation', [
      'SET_AMOUNT',
      'SET_AMOUNT_DESCRIPTION',
      'SET_FREQUENCY',
      'SET_NUM_CHARGES',
      'SET_SOLICITOR_SLUG',
      'SET_UTM_PARAMETERS',
      'SET_SOURCE_URL',
      'SET_PROGRAMMATIC_CURRENCY',
      'SET_PREFILL'
    ]),
    ...mapActions('donation', ['SET_CURRENCY_ACTIONS']),

    async handleDoubleQueryParams() {
      // Currently we handle param queries like double=true, double=donate, double=fundraiser, fundraiser=slug, solicitor=slug
      const queryParams = unparam(location.search);
      const { double, donsplus, fundraiser, solicitor, 'double-draft': doubleDraft, campaign, frequency } = queryParams;

      if (!double && !fundraiser && !solicitor && !donsplus && !doubleDraft && !campaign && !frequency) {
        return;
      }

      // ?donsplus=true is supported for legacy reasons
      const autoOpen =
        (double || donsplus) === 'true' ||
        double === 'donate' ||
        double === 'fundraise' ||
        (fundraiser && !this.isPages) ||
        (solicitor && !this.isPages) ||
        doubleDraft ||
        campaign ||
        frequency;
      if (autoOpen) {
        await this.$nextTick(); // In order to trigger the `widget` transition
        await this.setWidgetOpen(true);
      }

      if (fundraiser) {
        sessionStorage.setItem('Double.fundraiserSlug', fundraiser);
      }

      if (solicitor) {
        sessionStorage.setItem('Double.solicitorSlug', solicitor);
        this.SET_SOLICITOR_SLUG(solicitor);
      }

      const fundraiserSignup = double === 'fundraise';
      if (fundraiserSignup && !this.publicOrgSettings.p2pDisabled) {
        this.SET_WIDGET_VIEW(widgetViews.FUNDRAISER_SIGNUP);
      }

      const amount = queryParams.amount;
      if (amount && Number(amount) && Number(amount) >= 1 && Number(amount) <= MAX_AMOUNT) {
        this.SET_AMOUNT(amount);
      }

      if (queryParams?.frequency) {
        this.SET_FREQUENCY_ACTIONS(queryParams?.frequency);
      }

      if (queryParams?.summary === 'true') {
        this.SET_SHOW_PAYMENT_BUTTONS(true);
      }
    },
    handleFundraiserLink(fundraiserSlug) {
      // if (this.publicOrgSettings.p2pDisabled) {
      // return console.warn('Double: P2P is not enabled for this organization');
      // }

      sessionStorage.setItem('Double.fundraiserSlug', fundraiserSlug);
      return this.fetchFundraiser({ fundraiserSlug });
    },

    getUTMParameters() {
      const params = new Proxy(new URLSearchParams(window.location.search), {
        get: (searchParams, prop) => searchParams.get(prop)
      });

      // Standard UTM parameters
      const utmParameters = {
        utm_source: params.utm_source,
        utm_medium: params.utm_medium,
        utm_campaign: params.utm_campaign,
        utm_term: params.utm_term,
        utm_content: params.utm_content
      };
      try {
        const UTM_STORAGE_KEY = 'double_utm_parameters';
        if (Object.values(utmParameters).some((param) => param)) {
          sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmParameters));
        } else {
          const storedUtmParameters = sessionStorage.getItem(UTM_STORAGE_KEY);
          if (storedUtmParameters) {
            try {
              const parsedParams = JSON.parse(storedUtmParameters);
              Object.assign(utmParameters, parsedParams);
            } catch (parseError) {
              console.warn('Failed to parse stored UTM parameters:', parseError);
            }
          }
        }
      } catch (error) {
        console.warn(error);
      }

      return utmParameters;
    }
  },
  created() {
    const doubleApi = window.Double || {};
    const initialPrefill = doubleApi.prefill;

    Object.assign(doubleApi, {
      openCheckout: async (params) => {
        if (params?.campaign) {
          const campaignSlug = params?.campaign || this.globalSettings.defaultSlug;
          setCampaignInUrl(campaignSlug, true);
        }

        if (params?.tab) {
          this.SET_WIDGET_VIEW(widgetViews[params.tab]);
        }

        if (params?.amount) {
          this.SET_AMOUNT(params.amount);
        }

        if (params?.description) {
          this.SET_AMOUNT_DESCRIPTION(params.description);
        }

        if (params?.summary) {
          this.SET_SHOW_PAYMENT_BUTTONS(true);
        }

        if (params?.charges) {
          const charges = Number(params.charges);
          if (Number(charges || 0) < 2 || this.donation.frequency === 'single') {
            return;
          }

          this.SET_NUM_CHARGES(charges);
        }

        if (params?.currency) {
          this.SET_CURRENCY_ACTIONS(params.currency);
          this.SET_PROGRAMMATIC_CURRENCY(params.currency);
        }

        if (params?.prefill) {
          this.SET_PREFILL(params.prefill);
        }

        // await in order to have the campaign loaded
        await this.setWidgetOpen(true);

        if (params?.fundraiser) {
          await this.handleFundraiserLink(params.fundraiser);
        }

        if (params?.solicitor) {
          sessionStorage.setItem('Double.solicitorSlug', params.solicitor);
          this.SET_SOLICITOR_SLUG(params.solicitor);
        }

        // campaign settings will be available only after `setWidgetOpen` is called
        if (params?.frequency) {
          if (!this.campaign.settings.frequenciesEnabled.includes(params.frequency)) {
            return;
          }
          this.SET_FREQUENCY(params.frequency);
        }

        if (params?.otherAmount) {
          this.SET_CUSTOM_AMOUNT_OPEN(true);
        }
      },
      closeCheckout: () => this.setWidgetOpen(false)
    });

    Object.defineProperty(doubleApi, 'prefill', {
      configurable: true,
      enumerable: true,
      get: () => ({ ...this.donation.prefill }),
      set: (value) => {
        this.SET_PREFILL(value);
      }
    });

    window.Double = doubleApi;
    this.SET_PREFILL(initialPrefill);

    window.Double.openWidget = window.Double.openCheckout; // Legacy, don't remove!
    window.Double.closeWidget = window.Double.closeCheckout; // Legacy, don't remove!
    window.donsPlus = window.donsplus = window.Double; // Legacy, don't remove!

    const utms = this.getUTMParameters();
    this.SET_UTM_PARAMETERS(utms);
    this.SET_SOURCE_URL(window.location.href);
  },
  mounted() {
    window.Double.isReady = true;
    document.dispatchEvent(new CustomEvent('Double.ready'));
  }
};
